import React, { useEffect, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import TopAppBar from '../components/TopAppBar';
import BottomNavBar from '../components/BottomNavBar';
import { getMyDirectJobs } from '../api/directJob';
import { DirectJob, DirectJobStatus } from '../types/directJob';
import { useAuth } from '../context/AuthContext';

const statusConfig: Record<DirectJobStatus, { label: string; colour: string }> = {
  UnderReview: { label: 'Under Review', colour: 'bg-yellow-100 text-yellow-800' },
  Rejected: { label: 'Rejected', colour: 'bg-red-100 text-red-800' },
  Withdrawn: { label: 'Withdrawn', colour: 'bg-surface-container text-on-surface-variant' },
  PendingClient: { label: 'Awaiting Client', colour: 'bg-blue-100 text-blue-800' },
  Declined: { label: 'Declined', colour: 'bg-red-100 text-red-800' },
  Expired: { label: 'Expired', colour: 'bg-surface-container text-on-surface-variant' },
  Converted: { label: 'Accepted', colour: 'bg-green-100 text-green-800' },
};

const MyDirectJobsPage: React.FC = () => {
  const { currentUser } = useAuth();
  const [directJobs, setDirectJobs] = useState<DirectJob[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    getMyDirectJobs()
      .then(setDirectJobs)
      .catch(() => setError('Failed to load your Direct Jobs.'))
      .finally(() => setLoading(false));
  }, []);

  if (!currentUser?.isContractor) {
    return <Navigate to="/" replace />;
  }

  if (loading) return <div className="min-h-screen flex items-center justify-center">Loading...</div>;

  return (
    <div className="min-h-screen bg-surface-container-lowest font-sans antialiased">
      <TopAppBar />
      <main className="container mx-auto px-4 py-8 max-w-2xl">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-3xl font-bold text-on-surface">My Direct Jobs</h1>
          <Link
            to="/direct-jobs/new"
            className="flex items-center gap-1 px-4 py-2 bg-primary text-on-primary rounded-lg text-sm font-medium"
          >
            <span className="material-symbols-outlined text-[18px]">add</span>
            New
          </Link>
        </div>

        {error && <p className="text-error mb-4">{error}</p>}

        <div className="space-y-3">
          {directJobs.map((dj) => {
            // "Converted" covers two different outcomes - accepted, or declined-but-reopened -
            // so the label needs to check the underlying Quote status, not just the Direct Job status.
            const s = dj.status === 'Converted'
              ? dj.quote?.status === 'Accepted'
                ? { label: 'Accepted', colour: 'bg-green-100 text-green-800' }
                : { label: 'Reopened to market', colour: 'bg-blue-100 text-blue-800' }
              : statusConfig[dj.status];
            return (
              <Link
                key={dj.id}
                to={`/direct-jobs/${dj.id}`}
                className="block bg-surface rounded-xl border border-outline-variant p-5 hover:shadow-md transition-shadow"
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <h2 className="font-headline-sm text-primary truncate">{dj.title}</h2>
                    <p className="text-sm text-on-surface-variant mt-1">To: {dj.recipientEmail}</p>
                    {dj.quote && <p className="text-sm text-on-surface mt-1">R{dj.quote.total.toFixed(2)}</p>}
                  </div>
                  <span className={`text-xs px-2.5 py-1 rounded-full font-medium whitespace-nowrap ${s.colour}`}>{s.label}</span>
                </div>
              </Link>
            );
          })}
          {directJobs.length === 0 && (
            <div className="text-center py-12 bg-surface rounded-xl border border-outline-variant">
              <p className="text-on-surface-variant">You haven't created any Direct Jobs yet.</p>
              <Link to="/direct-jobs/new" className="text-primary hover:underline text-sm mt-2 inline-block">
                Create your first one
              </Link>
            </div>
          )}
        </div>
      </main>
      <BottomNavBar />
    </div>
  );
};

export default MyDirectJobsPage;
